import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { productCategoryService } from '../../services/productCategoryService';
import { ConfirmationModal } from '../common/ConfirmationModal';
import type { ProductCategory } from '../../types/product';

interface ProductCategoryManagerProps {
  businessProfileId: string;
  categories: ProductCategory[];
  onCategoriesChange: (categories: ProductCategory[]) => void;
}

// SW-94: create/rename/delete + drag-and-drop reorder for business-defined
// shop categories. Item-level reordering within a category is a separate
// concern, handled per-category in ProductsSection.
export function ProductCategoryManager({ businessProfileId, categories, onCategoriesChange }: ProductCategoryManagerProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { showToast } = useToast();

  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [deletingCategory, setDeletingCategory] = useState<ProductCategory | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleAdd = async () => {
    if (!token || !newName.trim()) return;
    setIsSaving(true);
    try {
      const created = await productCategoryService.createCategory(businessProfileId, { name: newName.trim() }, token);
      onCategoriesChange([...categories, created]);
      setNewName('');
      setIsAdding(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create category';
      showToast('error', t('common.error'), message);
    } finally {
      setIsSaving(false);
    }
  };

  const startEdit = (category: ProductCategory) => {
    setEditingId(category.id);
    setEditingName(category.name);
  };

  const saveEdit = async () => {
    if (!token || !editingId || !editingName.trim()) return;
    setIsSaving(true);
    try {
      const updated = await productCategoryService.updateCategory(
        businessProfileId, editingId, { name: editingName.trim() }, token
      );
      onCategoriesChange(categories.map(c => (c.id === updated.id ? updated : c)));
      setEditingId(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to rename category';
      showToast('error', t('common.error'), message);
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!token || !deletingCategory) return;
    setIsSaving(true);
    try {
      await productCategoryService.deleteCategory(businessProfileId, deletingCategory.id, token);
      onCategoriesChange(categories.filter(c => c.id !== deletingCategory.id));
      setDeletingCategory(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete category';
      showToast('error', t('common.error'), message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !token) return;

    const oldIndex = categories.findIndex(c => c.id === active.id);
    const newIndex = categories.findIndex(c => c.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(categories, oldIndex, newIndex);
    onCategoriesChange(reordered);

    try {
      await productCategoryService.reorderCategories(businessProfileId, reordered.map(c => c.id), token);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save category order';
      showToast('error', t('common.error'), message);
      onCategoriesChange(categories); // roll back to the pre-drag order
    }
  };

  return (
    <div className="bg-white/50 dark:bg-white/10 rounded-lg p-3 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="text-sm font-medium text-white/90">{t('products.categories.title', 'Kategorie')}</span>
          <p className="text-xs text-white/60 mt-0.5">
            {t(
              'products.categories.description',
              'Dodawaj i edytuj kategorie oraz ustalaj ich kolejność, przeciągając je w górę lub w dół.'
            )}
          </p>
        </div>
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="text-xs px-2 py-1 rounded bg-brand-primary text-white hover:bg-brand-primary-hover transition-colors flex-shrink-0"
          >
            {t('products.categories.add', '+ Kategoria')}
          </button>
        )}
      </div>

      {isAdding && (
        <div className="flex items-center gap-2">
          <input
            autoFocus
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            placeholder={t('products.categories.namePlaceholder', 'Nazwa kategorii')}
            className="flex-1 px-2 py-1 text-sm rounded bg-white dark:bg-white/10 border border-border-strong text-ink-primary"
          />
          <button onClick={handleAdd} disabled={isSaving || !newName.trim()} className="text-xs px-2 py-1 rounded bg-brand-confirm text-white disabled:opacity-50">
            {t('common.save', 'Zapisz')}
          </button>
          <button onClick={() => { setIsAdding(false); setNewName(''); }} className="text-xs px-2 py-1 text-white/70">
            {t('common.cancel', 'Anuluj')}
          </button>
        </div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={categories.map(c => c.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-1">
            {categories.map(category => (
              <SortableCategoryRow
                key={category.id}
                category={category}
                isEditing={editingId === category.id}
                editingName={editingName}
                onEditingNameChange={setEditingName}
                onStartEdit={() => startEdit(category)}
                onSaveEdit={saveEdit}
                onCancelEdit={() => setEditingId(null)}
                onDelete={() => setDeletingCategory(category)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {deletingCategory && (
        <ConfirmationModal
          title={t('products.categories.deleteConfirmTitle', 'Usuń kategorię')}
          message={
            deletingCategory.productCount > 0
              ? t('products.categories.deleteBlockedMessage', `Ta kategoria zawiera ${deletingCategory.productCount} pozycji. Przenieś je do innej kategorii, zanim ją usuniesz.`)
              : t('products.categories.deleteConfirmMessage', `Czy na pewno chcesz usunąć kategorię "${deletingCategory.name}"?`)
          }
          confirmLabel={t('common.delete', 'Usuń')}
          cancelLabel={t('common.cancel', 'Anuluj')}
          onConfirm={confirmDelete}
          onCancel={() => setDeletingCategory(null)}
          isLoading={isSaving}
          variant="danger"
        />
      )}
    </div>
  );
}

interface SortableCategoryRowProps {
  category: ProductCategory;
  isEditing: boolean;
  editingName: string;
  onEditingNameChange: (name: string) => void;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onDelete: () => void;
}

function SortableCategoryRow({
  category, isEditing, editingName, onEditingNameChange, onStartEdit, onSaveEdit, onCancelEdit, onDelete,
}: SortableCategoryRowProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 bg-surface-card dark:bg-white/10 rounded px-2 py-1.5"
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-ink-muted touch-none"
        aria-label={t('products.categories.dragHandle', 'Przeciągnij, aby zmienić kolejność')}
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M7 4a1 1 0 11-2 0 1 1 0 012 0zM7 10a1 1 0 11-2 0 1 1 0 012 0zM7 16a1 1 0 11-2 0 1 1 0 012 0zM15 4a1 1 0 11-2 0 1 1 0 012 0zM15 10a1 1 0 11-2 0 1 1 0 012 0zM15 16a1 1 0 11-2 0 1 1 0 012 0z" /></svg>
      </button>

      {isEditing ? (
        <>
          <input
            autoFocus
            value={editingName}
            onChange={e => onEditingNameChange(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && onSaveEdit()}
            className="flex-1 px-2 py-0.5 text-sm rounded border border-border-strong text-ink-primary"
          />
          <button onClick={onSaveEdit} className="text-xs text-brand-confirm-deep">{t('common.save', 'Zapisz')}</button>
          <button onClick={onCancelEdit} className="text-xs text-ink-muted">{t('common.cancel', 'Anuluj')}</button>
        </>
      ) : (
        <>
          <span className="flex-1 text-sm text-ink-primary">{category.name}</span>
          <span className="text-xs text-ink-muted">{category.productCount}</span>
          <button onClick={onStartEdit} className="text-xs text-ink-secondary hover:text-brand-primary px-1">
            {t('common.edit', 'Edytuj')}
          </button>
          <button onClick={onDelete} className="text-xs text-ink-secondary hover:text-brand-warm-deep px-1">
            {t('common.delete', 'Usuń')}
          </button>
        </>
      )}
    </div>
  );
}
